/**
 * Enhanced Search Optimizations
 * Improves predictive search with better UX, performance, and accessibility
 */

class EnhancedSearch {
  constructor() {
    this.RECENT_SEARCHES_KEY = 'theme_recent_searches';
    this.MAX_RECENT_SEARCHES = 5;
    this.DEBOUNCE_DELAY = 150;

    this.init();
  }

  init() {
    this.setupKeyboardShortcuts();
    this.enhancePredictiveSearch();
    this.addRecentSearches();
    this.improveSearchResults();
    this.addSearchAnalytics();
  }

  /**
   * Add keyboard shortcuts for search (Cmd/Ctrl + K)
   */
  setupKeyboardShortcuts() {
    document.addEventListener('keydown', (e) => {
      // Cmd+K or Ctrl+K to open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        const searchToggle = document.querySelector('.header__icon--search');
        if (searchToggle) {
          searchToggle.click();
          // Focus input after modal opens
          setTimeout(() => {
            const searchInput = document.querySelector('.search__input');
            if (searchInput) searchInput.focus();
          }, 100);
        }
      }

      // ESC to close search
      if (e.key === 'Escape') {
        const searchModal = document.querySelector('details-modal.header__search details[open]');
        if (searchModal) {
          searchModal.removeAttribute('open');
        }
      }
    });
  }

  /**
   * Enhance predictive search with better debouncing and caching
   */
  enhancePredictiveSearch() {
    const predictiveSearchElements = document.querySelectorAll('predictive-search');

    predictiveSearchElements.forEach(element => {
      const input = element.querySelector('.search__input');
      if (!input) return;

      let debounceTimer;
      const originalOnChange = element.onChange?.bind(element);

      // Better debouncing
      input.addEventListener('input', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => {
          if (originalOnChange) originalOnChange();
        }, this.DEBOUNCE_DELAY);
      });

      // Add loading indicator improvements
      const observer = new MutationObserver(() => {
        if (element.hasAttribute('loading')) {
          input.classList.add('search--loading');
        } else {
          input.classList.remove('search--loading');
        }
      });

      observer.observe(element, {
        attributes: true,
        attributeFilter: ['loading']
      });
    });
  }

  /**
   * Add recent searches functionality
   */
  addRecentSearches() {
    const searchForms = document.querySelectorAll('form[action*="/search"]');

    searchForms.forEach(form => {
      form.addEventListener('submit', (e) => {
        const input = form.querySelector('input[name="q"]');
        if (input && input.value.trim()) {
          this.saveRecentSearch(input.value.trim());
        }
      });
    });

    // Show recent searches when search input is focused and empty
    const searchInputs = document.querySelectorAll('.search__input');
    searchInputs.forEach(input => {
      input.addEventListener('focus', () => {
        if (!input.value.trim()) {
          this.showRecentSearches(input);
        }
      });
    });
  }

  /**
   * Save a search term to recent searches
   */
  saveRecentSearch(term) {
    try {
      let recentSearches = this.getRecentSearches();

      // Remove if already exists (to move to front)
      recentSearches = recentSearches.filter(s => s.toLowerCase() !== term.toLowerCase());

      // Add to front
      recentSearches.unshift(term);

      // Limit to max
      recentSearches = recentSearches.slice(0, this.MAX_RECENT_SEARCHES);

      localStorage.setItem(this.RECENT_SEARCHES_KEY, JSON.stringify(recentSearches));
    } catch (e) {
      console.warn('Unable to save recent search:', e);
    }
  }

  /**
   * Get recent searches from localStorage
   */
  getRecentSearches() {
    try {
      const stored = localStorage.getItem(this.RECENT_SEARCHES_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Show recent searches in dropdown
   */
  showRecentSearches(input) {
    const recentSearches = this.getRecentSearches();
    if (recentSearches.length === 0) return;

    const predictiveSearch = input.closest('predictive-search');
    if (!predictiveSearch) return;

    const resultsContainer = predictiveSearch.querySelector('[data-predictive-search]');
    if (!resultsContainer) return;

    const recentSearchesHTML = `
      <div class="predictive-search__recent">
        <div class="predictive-search__heading text-body caption-with-letter-spacing">
          Recent searches
        </div>
        <ul class="predictive-search__results-list list-unstyled" role="group">
          ${recentSearches.map((term, index) => `
            <li class="predictive-search__list-item" role="option">
              <button
                type="button"
                class="predictive-search__item predictive-search__item--recent link link--text"
                data-recent-search="${term}"
              >
                <svg class="icon icon-search" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M7 13A6 6 0 1 0 7 1a6 6 0 0 0 0 12zM15 15l-4.35-4.35" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                </svg>
                <span>${term}</span>
                <button
                  type="button"
                  class="predictive-search__recent-remove"
                  data-remove-search="${index}"
                  aria-label="Remove recent search"
                >
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                  </svg>
                </button>
              </button>
            </li>
          `).join('')}
        </ul>
        <button type="button" class="predictive-search__clear-recent link link--text">
          Clear recent searches
        </button>
      </div>
    `;

    resultsContainer.innerHTML = recentSearchesHTML;
    predictiveSearch.setAttribute('open', '');

    // Handle recent search clicks
    resultsContainer.querySelectorAll('[data-recent-search]').forEach(button => {
      button.addEventListener('click', () => {
        const term = button.getAttribute('data-recent-search');
        input.value = term;
        input.form.submit();
      });
    });

    // Handle remove individual search
    resultsContainer.querySelectorAll('[data-remove-search]').forEach(button => {
      button.addEventListener('click', (e) => {
        e.stopPropagation();
        const index = parseInt(button.getAttribute('data-remove-search'));
        this.removeRecentSearch(index);
        this.showRecentSearches(input);
      });
    });

    // Handle clear all
    const clearButton = resultsContainer.querySelector('.predictive-search__clear-recent');
    if (clearButton) {
      clearButton.addEventListener('click', () => {
        this.clearRecentSearches();
        predictiveSearch.removeAttribute('open');
        resultsContainer.innerHTML = '';
      });
    }
  }

  /**
   * Remove a recent search by index
   */
  removeRecentSearch(index) {
    try {
      let recentSearches = this.getRecentSearches();
      recentSearches.splice(index, 1);
      localStorage.setItem(this.RECENT_SEARCHES_KEY, JSON.stringify(recentSearches));
    } catch (e) {
      console.warn('Unable to remove recent search:', e);
    }
  }

  /**
   * Clear all recent searches
   */
  clearRecentSearches() {
    try {
      localStorage.removeItem(this.RECENT_SEARCHES_KEY);
    } catch (e) {
      console.warn('Unable to clear recent searches:', e);
    }
  }

  /**
   * Improve search results display
   */
  improveSearchResults() {
    // Observe for new search results
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach(node => {
            if (node.nodeType === 1 && node.matches('#predictive-search-results-groups-wrapper')) {
              this.enhanceSearchResultsDisplay(node);
            }
          });
        }
      });
    });

    const predictiveSearchElements = document.querySelectorAll('[data-predictive-search]');
    predictiveSearchElements.forEach(element => {
      observer.observe(element, {
        childList: true,
        subtree: true
      });
    });
  }

  /**
   * Enhance search results display with animations and better formatting
   */
  enhanceSearchResultsDisplay(resultsWrapper) {
    // Add staggered animation to results
    const resultItems = resultsWrapper.querySelectorAll('.predictive-search__list-item');
    resultItems.forEach((item, index) => {
      item.style.animationDelay = `${index * 30}ms`;
      item.classList.add('search-result--animated');
    });

    // Highlight matching text
    this.highlightSearchTerms(resultsWrapper);

    // Add product availability indicators
    this.addAvailabilityIndicators(resultsWrapper);
  }

  /**
   * Highlight search terms in results
   */
  highlightSearchTerms(container) {
    const searchInput = document.querySelector('.search__input');
    if (!searchInput || !searchInput.value.trim()) return;

    const searchTerm = searchInput.value.trim();
    const terms = searchTerm.split(' ').filter(t => t.length > 1);

    const headings = container.querySelectorAll('.predictive-search__item-heading');
    headings.forEach(heading => {
      let html = heading.innerHTML;

      terms.forEach(term => {
        const regex = new RegExp(`(${this.escapeRegExp(term)})`, 'gi');
        html = html.replace(regex, '<mark class="search-highlight">$1</mark>');
      });

      heading.innerHTML = html;
    });
  }

  /**
   * Add availability indicators to product results
   */
  addAvailabilityIndicators(container) {
    const productLinks = container.querySelectorAll('.predictive-search__item--link-with-thumbnail');
    productLinks.forEach(link => {
      // Check if product has price (likely available)
      const hasPrice = link.querySelector('.price');
      const hasSoldOut = link.textContent.includes('Sold out');

      if (hasSoldOut) {
        const indicator = document.createElement('span');
        indicator.className = 'search-availability search-availability--sold-out';
        indicator.textContent = 'Sold out';
        link.querySelector('.predictive-search__item-content')?.appendChild(indicator);
      }
    });
  }

  /**
   * Add basic search analytics
   */
  addSearchAnalytics() {
    const searchForms = document.querySelectorAll('form[action*="/search"]');

    searchForms.forEach(form => {
      form.addEventListener('submit', () => {
        const input = form.querySelector('input[name="q"]');
        if (input && input.value.trim()) {
          // Track search event (integrate with your analytics)
          this.trackSearchEvent(input.value.trim());
        }
      });
    });
  }

  /**
   * Track search event (placeholder for analytics integration)
   */
  trackSearchEvent(term) {
    // Log to console for debugging
    console.log('Search performed:', term);

    // If you have analytics (GA4, etc.), track here:
    // Example: gtag('event', 'search', { search_term: term });
    // Example: window.dataLayer?.push({ event: 'search', search_term: term });
  }

  /**
   * Escape special regex characters
   */
  escapeRegExp(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new EnhancedSearch();
  });
} else {
  new EnhancedSearch();
}
